import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Center,
  CloseButton,
  Code,
  Group,
  Loader,
  SegmentedControl,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { IconAlertCircle, IconPencil, IconPlus, IconSearch } from '@tabler/icons-react';
import { PageHeader } from '../../shared/components/PageHeader';
import { ApiError } from '../../shared/api/problem';
import { type WarehouseDto } from './api';
import { WAREHOUSES_KEY, useWarehouses } from './useWarehouses';
import { WarehouseFormModal } from './WarehouseFormModal';

// The hook fetches the full list (active + inactive) once and we narrow it
// client-side, so toggling Active/All is instant and never refetches. Warehouse
// counts per tenant are small, so this is cheaper than round-tripping.

type Scope = 'active' | 'all';

export function WarehousesRoute() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [modalOpen, { open, close }] = useDisclosure(false);
  const [editing, setEditing] = useState<WarehouseDto | null>(null);
  const [scope, setScope] = useState<Scope>('active');
  const [search, setSearch] = useState('');

  const query = useWarehouses();

  const rows = useMemo(() => {
    const all = query.data ?? [];
    const q = search.trim().toLowerCase();
    return all.filter((w) => {
      if (scope === 'active' && !w.isActive) return false;
      if (!q) return true;
      return (
        w.warehouseCode.toLowerCase().includes(q) ||
        w.displayName.toLowerCase().includes(q)
      );
    });
  }, [query.data, scope, search]);

  const inactiveCount = useMemo(
    () => (query.data ?? []).filter((w) => !w.isActive).length,
    [query.data],
  );

  function startCreate() {
    setEditing(null);
    open();
  }
  function startEdit(w: WarehouseDto) {
    setEditing(w);
    open();
  }
  function onSaved() {
    void queryClient.invalidateQueries({ queryKey: WAREHOUSES_KEY });
  }

  return (
    <>
      <PageHeader
        title={t('nav.warehouses')}
        actions={
          <Button color="invetrak" leftSection={<IconPlus size={16} />} onClick={startCreate}>
            {t('warehouses.new')}
          </Button>
        }
      />

      {query.isLoading && (
        <Center py="xl">
          <Loader color="invetrak" />
        </Center>
      )}

      {query.isError && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          variant="light"
          title={t('warehouses.errors.loadFailed')}
        >
          <Stack gap="xs">
            <Text size="sm">{(query.error as Error).message}</Text>
            {query.error instanceof ApiError && query.error.problem.correlationId && (
              <Text size="xs" c="dimmed">
                {t('errors.genericBody')} <Code>{query.error.problem.correlationId}</Code>
              </Text>
            )}
            <Group>
              <Button size="xs" variant="default" onClick={() => void query.refetch()}>
                {t('warehouses.retry')}
              </Button>
            </Group>
          </Stack>
        </Alert>
      )}

      {query.isSuccess && query.data.length === 0 && (
        <Card withBorder radius="md" padding="xl">
          <Stack gap="sm" align="center">
            <Text fw={600}>{t('warehouses.emptyTitle')}</Text>
            <Text size="sm" c="dimmed" ta="center">
              {t('warehouses.emptyBody')}
            </Text>
            <Button color="invetrak" leftSection={<IconPlus size={16} />} onClick={startCreate}>
              {t('warehouses.new')}
            </Button>
          </Stack>
        </Card>
      )}

      {query.isSuccess && query.data.length > 0 && (
        <Stack gap="md">
          <Group justify="space-between" wrap="wrap" gap="sm">
            <Group gap="sm">
              <SegmentedControl
                value={scope}
                onChange={(v) => setScope(v as Scope)}
                data={[
                  { label: t('warehouses.scopeActive'), value: 'active' },
                  {
                    label:
                      inactiveCount > 0
                        ? t('warehouses.scopeAllCount', { count: inactiveCount })
                        : t('warehouses.scopeAll'),
                    value: 'all',
                  },
                ]}
              />
              <Text size="sm" c="dimmed">
                {t('warehouses.count', { count: rows.length })}
              </Text>
            </Group>
            <TextInput
              placeholder={t('warehouses.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              leftSection={<IconSearch size={16} />}
              rightSection={
                search ? (
                  <CloseButton
                    size="sm"
                    onClick={() => setSearch('')}
                    aria-label={t('warehouses.searchClear')}
                  />
                ) : null
              }
              w={{ base: '100%', sm: 260 }}
            />
          </Group>

          <Card withBorder radius="md" padding={0}>
            {rows.length === 0 ? (
              <Center py="xl">
                <Stack gap="xs" align="center">
                  <Text c="dimmed" size="sm">
                    {search.trim() ? t('warehouses.noResults') : t('warehouses.noActive')}
                  </Text>
                  {!search.trim() && scope === 'active' && inactiveCount > 0 && (
                    <Button
                      size="xs"
                      variant="light"
                      color="invetrak"
                      onClick={() => setScope('all')}
                    >
                      {t('warehouses.showAll')}
                    </Button>
                  )}
                </Stack>
              </Center>
            ) : (
              <Table highlightOnHover verticalSpacing="sm" horizontalSpacing="md">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('warehouses.code')}</Table.Th>
                    <Table.Th>{t('warehouses.name')}</Table.Th>
                    <Table.Th>{t('warehouses.timeZone')}</Table.Th>
                    <Table.Th>{t('warehouses.currency')}</Table.Th>
                    <Table.Th>{t('warehouses.status')}</Table.Th>
                    <Table.Th w={60} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rows.map((w) => (
                    <Table.Tr key={w.warehouseId}>
                      <Table.Td>
                        <Code>{w.warehouseCode}</Code>
                      </Table.Td>
                      <Table.Td>{w.displayName}</Table.Td>
                      <Table.Td>{w.timeZone}</Table.Td>
                      <Table.Td>{w.currencyCode}</Table.Td>
                      <Table.Td>
                        <Badge color={w.isActive ? 'green' : 'gray'} variant="light">
                          {w.isActive ? t('warehouses.activeYes') : t('warehouses.activeNo')}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <ActionIcon
                          variant="subtle"
                          color="invetrak"
                          aria-label={t('shell.edit')}
                          onClick={() => startEdit(w)}
                        >
                          <IconPencil size={16} />
                        </ActionIcon>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Card>
        </Stack>
      )}

      <WarehouseFormModal opened={modalOpen} onClose={close} warehouse={editing} onSaved={onSaved} />
    </>
  );
}
